/* GRO — full-page search panel (search option D, Oct 2026).

   Markup: snippets/gro-search-panel.liquid (rendered once, end of <body>).
   Body:   sections/gro-search-results.liquid (cards), sections/gro-search-rows.liquid
           (phones while typing: rows only) and sections/gro-search-suggest.liquid,
           fetched with the Section Rendering API — never placed in a template.

   Before typing, the body is a collection rendered through the results section
   (Best sellers, or the Women list once the shopper picks Women). While typing,
   it is /search?q=…&type=product rendered through the same section, so the
   products, the count and the Category / Colour / Size filters all come from
   Shopify's own search — the same results as the search page — plus a parallel
   predictive-search request for suggestions and matching collections.

   State kept here:
     term       what is in the box (trimmed)
     filters    Category / Colour / Size picks for the current term (cleared when it changes)
     shopFor    'all' | 'women' — remembered across pages (localStorage gro:shop-for)
   The Product / Model and grid-size controls are the collection grid's own
   (gro-grid-view.js); the saved choice is applied to each new body before it
   is shown.

   Speed: the opening list is fetched once the page has settled, so the icon opens
   straight onto it; a popular search or suggestion starts loading on hover, press or
   focus, so the tap lands on a ready answer; answers are kept (CACHE_MAX) for
   backspacing; and a hairline runs under the field from the first keystroke until
   the answer is drawn (.is-pending / .is-loading in gro-search.css).

   Analytics: a search counts once it has shown results and the shopper has
   stopped typing for SETTLE ms, or leaves the panel / clicks a result before
   that. It is announced as a `gro:search` DOM event; gro-analytics.js sends it
   to GA4. */
(() => {
  if (customElements.get('gro-search-panel')) return;

  const SHOP_FOR_KEY = 'gro:shop-for';
  const VIEW_KEY = 'gro:grid-view';
  const TEST_KEY = 'gro:grid-test';
  const SHOP_FOR_PARAM = 'filter.p.m.gro.shop_for';
  const DEBOUNCE_MS = 150;
  const HOVER_PREFETCH_MS = 80;
  const SETTLE_MS = 1200;
  const CACHE_MAX = 40;
  const PHONE = window.matchMedia('(max-width: 749px)');

  const storage = {
    get(key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        /* private mode — the choice just won't persist */
      }
    },
    json(key) {
      try {
        return JSON.parse(localStorage.getItem(key)) || {};
      } catch (e) {
        return {};
      }
    },
  };

  class GroSearchPanel extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[role="dialog"]');
      this.form = this.querySelector('[data-gro-search-form]');
      this.input = this.querySelector('[data-gro-search-input]');
      this.clearButton = this.querySelector('[data-gro-search-clear]');
      this.body = this.querySelector('[data-gro-search-body]');
      this.status = this.querySelector('[data-gro-search-status]');

      this.cache = new Map();
      this.prefetching = new Map();
      this.filters = new Map();
      this.reported = new Set();
      this.shopFor = storage.get(SHOP_FOR_KEY) === 'women' ? 'women' : 'all';
      this.term = '';
      this.renderedTerm = '';
      this.renderedCount = 0;

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-gro-search-open]');
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      });
      this.querySelector('[data-gro-search-close]').addEventListener('click', () => this.close());
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.input.addEventListener('input', () => this.onInput(false));
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.onInput(true);
        this.input.focus();
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.goToResults();
      });
      this.body.addEventListener('click', (event) => this.onBodyClick(event));

      /* Start fetching a popular search or suggestion before it is tapped: on a short
         hover with a mouse, and at once on press or keyboard focus */
      this.body.addEventListener('pointerover', (event) => {
        clearTimeout(this.hoverTimer);
        const link = event.pointerType === 'mouse' && event.target.closest('[data-gro-q]');
        if (link) this.hoverTimer = setTimeout(() => this.prefetchTerm(link.dataset.groQ), HOVER_PREFETCH_MS);
      });
      this.body.addEventListener('pointerout', (event) => {
        const link = event.target.closest('[data-gro-q]');
        if (link && !link.contains(event.relatedTarget)) clearTimeout(this.hoverTimer);
      });
      this.body.addEventListener('pointerdown', (event) => this.prefetchFrom(event.target));
      this.body.addEventListener('focusin', (event) => this.prefetchFrom(event.target));

      /* Typing results come as rows on phones and as cards elsewhere, so crossing the
         breakpoint (rotating a phone, resizing a window) fetches the other kind */
      PHONE.addEventListener('change', () => {
        if (this.isOpen && this.term) this.refresh();
      });

      /* Phones (Tyrone, 12 Oct): putting the keyboard away means the shopper has
         finished typing, so the full results page opens. That is Done on the keyboard,
         starting to scroll the list, or (Android) the keyboard being closed. A timer
         would fire on ordinary pauses: between words, or while picking Chinese
         characters. A tap on a link or button (a product, a suggestion, CLOSE, clear)
         does its own thing instead. */
      this.input.addEventListener('blur', () => this.onInputBlur());
      this.dialog.addEventListener(
        'pointerdown',
        (event) => {
          this.lastTap = {
            at: Date.now(),
            control: Boolean(event.target.closest('a[href], button, input, select, summary, label')),
          };
        },
        true
      );
      this.body.addEventListener('touchstart', (event) => {
        this.touchY = event.touches[0].clientY;
      }, { passive: true });
      this.body.addEventListener('touchmove', (event) => {
        if (!PHONE.matches || document.activeElement !== this.input) return;
        if (Math.abs(event.touches[0].clientY - this.touchY) > 12) {
          this.keyboardAway = true;
          this.input.blur();
        }
      }, { passive: true });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.leaving = false;
      });

      this.warmUp();
    }

    /* Fetch the opening list (Best sellers, or Women) once the page has settled, so the
       search icon opens straight onto it. Skipped when the shopper's browser asks to
       save data. */
    warmUp() {
      const connection = navigator.connection;
      if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return;
      const start = () => {
        const whenIdle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1500));
        whenIdle(
          () => {
            if (!this.body.childElementCount) this.prefetch(this.urls('').results);
          },
          { timeout: 4000 }
        );
      };
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    }

    get isOpen() {
      return !this.hidden;
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.hidden = false;
      document.body.classList.add('overflow-hidden', 'gro-search-open');
      this.trackViewport(true);
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.input.focus({ preventScroll: true });
      });
      if (!this.body.childElementCount || this.body.querySelector('[data-mode="fallback"]')) this.refresh();
    }

    close() {
      if (!this.isOpen) return;
      this.report();
      this.classList.remove('is-open');
      this.hidden = true;
      document.body.classList.remove('overflow-hidden', 'gro-search-open');
      this.trackViewport(false);
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus({ preventScroll: true });
    }

    /* On phones the keyboard covers the bottom of a fixed panel without resizing it,
       so the "See all" bar would sit behind the keys. While open, the panel is sized
       to the visual viewport: the part of the screen above the keyboard. */
    trackViewport(on) {
      const vv = window.visualViewport;
      if (!vv) return;
      if (!this.syncViewport) {
        this.syncViewport = () => {
          this.style.setProperty('--gro-vv-top', `${vv.offsetTop}px`);
          this.style.setProperty('--gro-vv-height', `${vv.height}px`);
          /* Android closes the keyboard without blurring the field: the visual viewport
             grows back at the same width. Treat that like Done. */
          const last = this.vvSize;
          this.vvSize = { width: vv.width, height: vv.height };
          if (
            last &&
            PHONE.matches &&
            Math.abs(vv.width - last.width) < 2 &&
            vv.height - last.height > 150 &&
            document.activeElement === this.input
          ) {
            this.keyboardAway = true;
            this.input.blur();
          }
        };
      }
      if (on) {
        this.syncViewport();
        vv.addEventListener('resize', this.syncViewport);
        vv.addEventListener('scroll', this.syncViewport);
      } else {
        vv.removeEventListener('resize', this.syncViewport);
        vv.removeEventListener('scroll', this.syncViewport);
        this.vvSize = null;
        this.style.removeProperty('--gro-vv-top');
        this.style.removeProperty('--gro-vv-height');
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    trapFocus(event) {
      const focusables = Array.from(
        this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"])')
      ).filter((el) => !el.hidden && el.getClientRects().length > 0);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onInput(immediately) {
      const term = this.input.value.trim();
      this.clearButton.hidden = this.input.value === '';
      if (term === this.term) return;
      this.term = term;
      clearTimeout(this.settleTimer);
      clearTimeout(this.debounceTimer);
      this.filters.clear();
      /* The hairline starts with the keystroke rather than after the pause, unless the
         answer is already here (it would only flicker) */
      this.classList.toggle('is-pending', !this.cache.has(this.urls(term, this.filters).results));
      this.debounceTimer = setTimeout(() => this.refresh(), immediately ? 0 : DEBOUNCE_MS);
    }

    searchParams(term = this.term, filters = this.filters) {
      const params = new URLSearchParams();
      params.set('q', term);
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      if (this.shopFor === 'women') params.append(SHOP_FOR_PARAM, 'Women');
      filters.forEach((values, param) => values.forEach((value) => params.append(param, value)));
      return params;
    }

    /* The requests behind one view of the panel. Before typing: the opening list as
       cards. While typing: Shopify search, as rows on phones (gro-search-rows) and as
       cards with the refine filters elsewhere, plus predictive search for the
       suggestions and the collection link. */
    urls(term, filters) {
      if (!term) {
        const base = this.shopFor === 'women' ? this.dataset.womenUrl : this.dataset.idleUrl;
        return { results: `${base}?section_id=gro-search-results`, suggest: null };
      }
      const params = this.searchParams(term, filters);
      params.set('section_id', PHONE.matches ? 'gro-search-rows' : 'gro-search-results');
      const suggest = new URLSearchParams();
      suggest.set('q', term);
      suggest.set('section_id', 'gro-search-suggest');
      suggest.set('resources[type]', 'query,collection');
      suggest.set('resources[limit]', '6');
      suggest.set('resources[limit_scope]', 'each');
      return {
        results: `${this.dataset.searchUrl}?${params}`,
        suggest: `${this.dataset.suggestUrl}?${suggest}`,
      };
    }

    resultsHref() {
      return `${this.dataset.searchUrl}?${this.searchParams()}`;
    }

    /* The results page for a tapped term, products only, with Shopify's search tracking
       (_pos, _psq, _ss) carried over from the suggestion's own link when it has one */
    goToTerm(term, href) {
      const url = new URL(`${this.dataset.searchUrl}?${this.searchParams(term, new Map())}`, window.location.href);
      if (href) {
        new URL(href, window.location.href).searchParams.forEach((value, name) => {
          if (name.startsWith('_')) url.searchParams.set(name, value);
        });
      }
      this.leaving = true;
      this.report();
      window.location.href = url.pathname + url.search;
    }

    async refresh() {
      const typing = this.term.length > 0;
      const { results: resultsUrl, suggest: suggestUrl } = this.urls(this.term, this.filters);

      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.body.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');

      try {
        const [results, suggestions] = await Promise.all([
          this.fetchSection(resultsUrl, controller),
          suggestUrl ? this.fetchSection(suggestUrl, controller).catch(() => '') : Promise.resolve(''),
        ]);
        if (controller !== this.controller) return;
        this.render(results, suggestions, typing);
      } catch (error) {
        /* Aborted by newer typing, or the request failed (offline, bot check). The
           previous body stays and Enter still goes to the full results page; an empty
           panel gets the popular searches as plain links. */
        if (error.name !== 'AbortError' && !this.body.childElementCount) {
          const fallback = this.querySelector('[data-gro-search-fallback]');
          if (fallback) this.body.replaceChildren(fallback.content.cloneNode(true));
        }
      } finally {
        if (controller === this.controller) {
          this.body.setAttribute('aria-busy', 'false');
          this.classList.remove('is-loading', 'is-pending');
        }
      }
    }

    async fetchSection(url, controller) {
      if (this.cache.has(url)) return this.cache.get(url);
      /* The warm-up or a hover may already be fetching it */
      const pending = this.prefetching.get(url);
      if (pending) {
        const ready = await pending;
        if (ready !== null) return ready;
      }
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = this.sectionHtml(await response.text());
      if (html === null) throw new Error('No section in response');
      return this.store(url, html);
    }

    /* Fetch ahead into the cache. Never aborted and never throws: if it fails or is
       still out, refresh() fetches for itself. */
    prefetch(url) {
      if (!url || this.cache.has(url) || this.prefetching.has(url)) return;
      const job = fetch(url, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : null))
        .then((text) => {
          const html = text === null ? null : this.sectionHtml(text);
          return html === null ? null : this.store(url, html);
        })
        .catch(() => null)
        .finally(() => this.prefetching.delete(url));
      this.prefetching.set(url, job);
    }

    prefetchTerm(term) {
      const value = (term || '').trim();
      if (!value || value === this.term) return;
      const { results, suggest } = this.urls(value, new Map());
      this.prefetch(results);
      this.prefetch(suggest);
    }

    prefetchFrom(target) {
      /* On phones a tap opens the results page, so there is nothing to fetch ahead here */
      if (PHONE.matches) return;
      const link = target && target.closest && target.closest('[data-gro-q]');
      if (link) this.prefetchTerm(link.dataset.groQ);
    }

    sectionHtml(text) {
      const section = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
      return section ? section.innerHTML : null;
    }

    store(url, html) {
      this.cache.set(url, html);
      if (this.cache.size > CACHE_MAX) this.cache.delete(this.cache.keys().next().value);
      return html;
    }

    applySavedView(view) {
      const saved = storage.json(VIEW_KEY);
      const test = storage.json(TEST_KEY);
      if (saved.photo === 'product' || saved.photo === 'model') {
        view.dataset.groPhoto = saved.photo;
        view.dataset.groViewSource = 'saved';
      } else if (test.arm === 'product' || test.arm === 'model') {
        view.dataset.groPhoto = test.arm;
        view.dataset.groViewSource = 'test';
      }
      if (['large', 'standard', 'compact'].includes(saved.density)) view.dataset.groDensity = saved.density;
      view.querySelectorAll('[data-gro-set-photo]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.groSetPhoto === view.dataset.groPhoto));
      });
      view.querySelectorAll('[data-gro-set-density]').forEach((button) => {
        const pressed =
          button.dataset.groSetDensity === view.dataset.groDensity || button.dataset.groAlso === view.dataset.groDensity;
        button.setAttribute('aria-pressed', String(pressed));
      });
    }

    render(results, suggestions, typing) {
      const template = document.createElement('template');
      template.innerHTML = results;
      const view = template.content.querySelector('.gro-view');
      if (view) this.applySavedView(view);
      const slot = template.content.querySelector('[data-gro-sp-suggest]');
      if (slot && suggestions) slot.innerHTML = suggestions;
      const seeAll = template.content.querySelector('[data-gro-see-all]');
      if (seeAll) seeAll.href = this.resultsHref();

      const scrollTop = this.body.scrollTop;
      const sameTerm = typing && this.renderedTerm === this.term;
      this.body.replaceChildren(template.content);
      /* Refining the same search keeps the shopper's place; a new search starts at the top. */
      this.body.scrollTop = sameTerm ? scrollTop : 0;

      /* Refine (category / colour / size) arrives open. On a phone it folds unless the
         shopper opened it, so the first products sit above the fold. */
      const refine = this.body.querySelector('[data-gro-refine]');
      if (refine) {
        if (PHONE.matches && !this.refineOpen) refine.removeAttribute('open');
        refine.addEventListener('toggle', () => {
          if (PHONE.matches) this.refineOpen = refine.open;
        });
      }

      const root = this.body.querySelector('[data-gro-sp]');
      this.renderedTerm = typing ? this.term : '';
      this.renderedCount = root && root.dataset.count ? parseInt(root.dataset.count, 10) || 0 : 0;
      this.renderedMembers = Boolean(root && root.dataset.members);
      const heading = this.body.querySelector('#GroSpHeading');
      this.status.textContent = heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '';

      clearTimeout(this.settleTimer);
      if (typing) this.settleTimer = setTimeout(() => this.report(), SETTLE_MS);
    }

    goToResults() {
      if (!this.term) {
        this.input.focus();
        return;
      }
      this.leaving = true;
      this.report();
      window.location.href = this.resultsHref();
    }

    /* The field lost focus on a phone. If the keyboard was put away (not a tap on a
       link or button), the shopper is done typing: open the full results, once the
       term has at least two characters and isn't known to find nothing. */
    onInputBlur() {
      const away = this.keyboardAway;
      this.keyboardAway = false;
      if (!PHONE.matches || !this.isOpen || this.leaving) return;
      const tap = this.lastTap;
      if (!away && tap && tap.control && Date.now() - tap.at < 700) return;
      clearTimeout(this.blurTimer);
      this.blurTimer = setTimeout(() => {
        if (!this.isOpen || this.leaving || document.visibilityState !== 'visible') return;
        if (document.activeElement === this.input) return;
        if (this.term.length < 2) return;
        if (this.renderedTerm === this.term && this.renderedCount === 0) return;
        this.goToResults();
      }, 250);
    }

    onBodyClick(event) {
      const suggestion = event.target.closest('[data-gro-q]');
      if (suggestion) {
        event.preventDefault();
        /* Phones (Tyrone, 12 Oct): a suggestion or a popular search opens its results
           page. Refining inside the panel kept narrowing the list under the keyboard. */
        if (PHONE.matches) {
          this.goToTerm(suggestion.dataset.groQ, suggestion.getAttribute('href'));
          return;
        }
        this.input.value = suggestion.dataset.groQ;
        this.onInput(true);
        this.input.focus({ preventScroll: true });
        return;
      }

      const shopFor = event.target.closest('[data-gro-shop-for]');
      if (shopFor) {
        const value = shopFor.dataset.groShopFor === 'women' ? 'women' : 'all';
        if (value !== this.shopFor) {
          this.shopFor = value;
          storage.set(SHOP_FOR_KEY, value);
          this.refresh();
        }
        return;
      }

      const filter = event.target.closest('[data-gro-filter]');
      if (filter) {
        const param = filter.dataset.groFilter;
        const value = filter.dataset.groValue;
        const values = this.filters.get(param) || new Set();
        if (values.has(value)) values.delete(value);
        else values.add(value);
        if (values.size) this.filters.set(param, values);
        else this.filters.delete(param);
        filter.setAttribute('aria-pressed', String(values.has(value)));
        this.refresh();
        return;
      }

      if (event.target.closest('a[href]')) this.report();
    }

    report() {
      clearTimeout(this.settleTimer);
      const term = this.renderedTerm;
      if (!term) return;
      const filterKey = Array.from(this.filters, ([param, values]) => `${param}=${Array.from(values).join(',')}`).join('&');
      const key = `${term.toLowerCase()}|${this.shopFor}|${filterKey}`;
      if (this.reported.has(key)) return;
      this.reported.add(key);
      document.dispatchEvent(
        new CustomEvent('gro:search', {
          detail: {
            term,
            count: this.renderedCount,
            shopFor: this.shopFor,
            filtered: this.filters.size > 0,
            membersOnly: this.renderedMembers,
          },
        })
      );
    }
  }

  customElements.define('gro-search-panel', GroSearchPanel);
})();
